/**
 * Where a replay's pixels come from, and the instants to decode them at.
 *
 * `recording` comes first, not alphabetically: the case that generated this
 * feature (a package at device 615's door, 2026-08-26 18:12) needs no upload
 * at all — the recorder writes `-c:v copy`, so the recorded frame IS the
 * native frame the detector would have seen live. An upload that re-shoots
 * the same scene from another rig is DIFFERENT pixels, and a replay over
 * different pixels answers a different question.
 *
 * `deviceId` is present on BOTH branches. For `recording` it is the camera
 * being read; for `upload` it is the camera whose PIPELINE is being
 * rigiocata — the clip is not a camera, but every log line this replay
 * produces is grouped by that `deviceId` regardless of where the pixels
 * came from (`CLAUDE.md`'s tagging rule).
 */
export type ReplaySource =
  | {
      readonly kind: 'recording'
      readonly deviceId: number
      readonly fromMs: number
      readonly toMs: number
    }
  | {
      readonly kind: 'upload'
      readonly clipId: string
      readonly deviceId: number
      readonly durationMs: number
    }

export type ReplayFramePlan =
  | {
      readonly kind: 'planned'
      readonly deviceId: number
      /** Absolute wall-clock ms for `recording`, offset-from-start ms for
       *  `upload` — see the source union's doc. */
      readonly instants: readonly number[]
      /** `true` iff `maxFrames` cut a longer plan short. A silently
       *  truncated plan and "nothing detected in that window" render as the
       *  same empty screen — this flag is the only thing that tells them
       *  apart before the operator draws the wrong conclusion. */
      readonly truncated: boolean
    }
  | {
      readonly kind: 'refused'
      /** Zero frames and "no detections" must never look alike — a refusal
       *  is the only way the caller can distinguish "there is nothing to
       *  decode" from "we decoded and the model saw nothing". */
      readonly reason: 'empty-window'
    }

/**
 * Turn a fixed input into the ordered instants to decode.
 *
 * @param fps sampling rate — frames per second of WALL-CLOCK duration,
 *   independent of the source's own frame rate (the decode step samples,
 *   never re-times — see `replay-decode.ts`).
 * @param maxFrames hard cap; a plan that would exceed it is TRUNCATED and
 *   says so (`truncated: true`), never silently shortened.
 */
export function planReplayFrames(
  source: ReplaySource,
  fps: number,
  maxFrames: number,
): ReplayFramePlan {
  const durationMs = source.kind === 'recording' ? source.toMs - source.fromMs : source.durationMs
  if (durationMs <= 0) return { kind: 'refused', reason: 'empty-window' }

  const stepMs = 1000 / fps
  const startAt = source.kind === 'recording' ? source.fromMs : 0
  const rawCount = Math.max(1, Math.floor(durationMs / stepMs))
  const truncated = rawCount > maxFrames
  const count = Math.min(rawCount, maxFrames)

  const instants: number[] = []
  for (let i = 0; i < count; i++) instants.push(startAt + Math.round(i * stepMs))

  return { kind: 'planned', deviceId: source.deviceId, instants, truncated }
}
